import React, { FC, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BackArrow, Button, ModalVariables, Tabs, TopProgress } from '../../../components'
import { staffObjectStorageService } from '../../../api'
import { StaffBucket } from '../../../api/staffObjectStorageService'
import { usePageTitle } from '../../../context/navigationContext'
import { useUi } from '../../../context/ui.context'
import { formatFileSize } from '../../../utils/sizes'
import BucketOverview from './bucket-overview'
import BucketFiles from './bucket-files'
import { BucketLifecycle, BucketPolicy } from './bucket-json'
import {
  DetailHeader,
  HeaderActions,
  HeaderText,
  MutedText,
  Navigation,
  PageContainer,
  PageTitle,
} from './styles'
import { getObjectStorageError } from './utils'

type Props = {
  bucketListPath: string
  userListPath: string
}

const BucketPage: FC<Props> = ({ bucketListPath, userListPath }) => {
  const [t] = useTranslation()
  const { bucketId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { viewAlert } = useUi()
  const [bucket, setBucket] = useState<StaffBucket | null>(null)
  const [pageLoading, setPageLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const lastPathPart = location.pathname.split('/').filter(Boolean).pop()
  const activeTab = ['overview', 'files', 'policy', 'lifecycle'].includes(lastPathPart || '')
    ? lastPathPart
    : 'overview'

  usePageTitle(bucket?.name
    ? `${t('PAGE_TITLE_STAFF_BUCKET')} - ${bucket.name}`
    : t('PAGE_TITLE_STAFF_BUCKET'))

  const loadBucket = () => {
    if (!bucketId) return
    staffObjectStorageService.getBucket(bucketId)
      .then(({ data }) => setBucket(data))
      .catch((error) => {
        viewAlert({
          severity: 'error',
          message: getObjectStorageError(error, t('ERROR_OCCURRED')),
        })
        navigate(bucketListPath)
      })
      .finally(() => setPageLoading(false))
  }

  useEffect(() => {
    loadBucket()
  }, [bucketId])

  const deleteBucket = async () => {
    if (!bucket) return
    try {
      setLoading(true)
      await staffObjectStorageService.deleteBucket(bucket.id)
      viewAlert({ severity: 'information', message: t('BUCKET_DELETED') })
      navigate(bucketListPath)
    } catch (error) {
      viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      })
    } finally {
      setLoading(false)
      setViewModal(false)
    }
  }

  if (pageLoading) return <TopProgress loading />
  if (!bucket || !bucketId) return null

  const tabs = [
    { id: '1', title: t('OVERVIEW'), slug: 'overview' },
    { id: '2', title: t('FILES'), slug: 'files' },
    { id: '3', title: t('BUCKET_POLICY'), slug: 'policy' },
    { id: '4', title: t('LIFECYCLE_RULES'), slug: 'lifecycle' },
  ]

  return (
    <PageContainer>
      <BackArrow fallbackPath={bucketListPath} />
      <DetailHeader>
        <HeaderText>
          <PageTitle>{bucket.name}</PageTitle>
          <MutedText>
            {bucket.client?.display || bucket.client?.name || t('NO_CLIENT')}
            {' / '}
            {bucket.object_user.ceph_uid || bucket.object_user.id}
            {' / '}
            {formatFileSize(bucket.usage)}
          </MutedText>
        </HeaderText>
        <HeaderActions>
          <Button
            variant="primary"
            size="display"
            title={t('DELETE_BUCKET')}
            onClick={() => setViewModal(true)}
          />
        </HeaderActions>
      </DetailHeader>
      <Navigation>
        <div>
          <Tabs
            tabs={tabs}
            size="display"
            location={activeTab}
            blockNavigate
            onChange={(tab) => navigate(`${bucketListPath}/${bucketId}/${tab}`)}
          />
        </div>
      </Navigation>
      <Routes>
        <Route path="/" element={<Navigate replace to="overview" />} />
        <Route
          path="/overview"
          element={<BucketOverview bucket={bucket} userListPath={userListPath} onUpdate={loadBucket} />}
        />
        <Route path="/files" element={<BucketFiles bucketId={bucketId} />} />
        <Route path="/policy" element={<BucketPolicy bucketId={bucketId} />} />
        <Route path="/lifecycle" element={<BucketLifecycle bucketId={bucketId} />} />
        <Route path="*" element={<Navigate replace to="overview" />} />
      </Routes>
      <ModalVariables
        modalType="delete"
        viewModal={viewModal}
        activeAnimation={viewModal}
        closeModal={deleteBucket}
        closeModalCancel={() => setViewModal(false)}
        alertTitle={t('BUCKET')}
        alertSubTitle={[bucket]}
        instance={bucket}
        loading={loading}
      />
    </PageContainer>
  )
}

export default BucketPage
